import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import {
  CameraOutlined,
  ArrowLeftOutlined,
  MoneyCollectOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  Table,
  Spin,
  Empty,
  message,
  Image,
  Card,
  Typography,
  Button,
  Tag,
  Row,
  Col,
  Descriptions,
  Input
} from 'antd';
import axiosInstance from '../../utils/axios';

const { Title, Text } = Typography;

const EventTicketSnapshotsDetail = () => {
  const { showId, dateId, timeslotId } = useParams();
  const location = useLocation();
const eventName = location.state?.eventName;
  const navigate = useNavigate();

  const [snapshots, setSnapshots] = useState([]);
  const [showInfo, setShowInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    fetchSnapshots(pagination.current, search);
  }, [ pagination.current, search]);

  const fetchSnapshots = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      // Backend event-scoped snapshots endpoint
      let url = `news_and_events/event-shows/event-ticket-snapshots/?event_heading=${encodeURIComponent(eventName || '')}`;
      const params = { page, page_size: pagination.pageSize };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await axiosInstance.get(url, { params });

      const data = response.data;
      const results = data.results || [];

      if (results.length > 0) {
        const first = results[0];
        const totalTickets = results.reduce((sum, s) => sum + (s.total_tickets || s.number_of_tickets || 0), 0);
        const totalAmount = results.reduce((sum, s) => sum + parseFloat(s.amount || s.amount_to_pay || 0), 0);
        const qrCount = results.filter(s => s.qr_code).length;
        const verifiedCount = results.filter(s => s.is_verified).length;
        setShowInfo({
          eventHeading: first.event_title || first.event?.heading || 'Unknown Event',
          eventId: first.event_id || first.event?.id,
          venue: first.venue,
          totalTickets,
          totalAmount: totalAmount.toFixed(2),
          qrCount,
          verifiedCount,
          currentPage: data.current_page,
          totalPages: data.total_pages,
          pageSize: data.page_size,
          count: data.count
        });
      }

      setSnapshots(results);
      setPagination({
        current: page,
        pageSize: pagination.pageSize,
        total: data.count || 0,
      });
    } catch (error) {
      message.error('Failed to load event ticket snapshots');
      console.error(error);
      setSnapshots([]);
      setShowInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      current: page,
      pageSize: pageSize || 10,
    }));
  };

  const handleSearch = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const getMediaPath = (fullUrl) => {
    if (!fullUrl) return '';
    try {
      const url = new URL(fullUrl);
      const index = url.pathname.indexOf('mediafiles');
      return index !== -1 ? url.pathname.substring(index) : url.pathname;
    } catch (e) {
      const index = fullUrl.indexOf('mediafiles');
      return index !== -1 ? fullUrl.substring(index) : fullUrl;
    }
  };

  const columns = [
    {
      title: 'QR Code',
      key: 'qr_code',
      align: 'center',
      render: (_, record) => {
        const qrSrc = getMediaPath(record.qr_code);
        return (
          <div style={{ padding: 8 }}>
            <Image
              src={`${process.env.REACT_APP_MEDIA_URL}${qrSrc}`}
              alt="QR Code"
              width={120}
              height={120}
              style={{ border: '1px solid #ddd', borderRadius: 8 }}
              preview={{ mask: <QrcodeOutlined style={{ fontSize: 20 }} /> }}
            />
          </div>
        );
      },
    },
    {
      title: 'Booked By',
      key: 'booked_by',
      render: (_, record) => (
        <div>
          <div>
            <UserOutlined style={{ marginRight: 6 }} />
            <strong>{record.booked_by_email || record.booked_by || 'N/A'}</strong>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.booked_by_phone || '-'}
          </Text>
        </div>
      ),
    },
    {
      title: 'Booking ID',
      dataIndex: 'booking_id',
      key: 'booking_id',
      render: (id) => <Tag color="purple">{id}</Tag>
    },
    {
      title: 'Tickets',
      key: 'tickets',
      render: (_, record) => (
        <Tag color="blue">
          {(record.total_tickets || (record.seats?.length ?? 0))} ticket{((record.number_of_tickets || (record.seats?.length ?? 0)) > 1) ? 's' : ''}
        </Tag>
      ),
    },
    {
      title: 'Sections / Seats',
      key: 'sections_seats',
      render: (_, record) => (
        <div style={{ fontSize: 13 }}>
            {(record.sections?.map((section, idx) => (
              <Tag key={`sec-${idx}`} style={{ margin: '2px 4px' }}>
                {section.title} x{section.quantity}
              </Tag>
            )) || record.seats?.map((seat, idx) => (
              <Tag key={`seat-${idx}`} style={{ margin: '2px 4px' }}>
                {seat.row || ''}{seat.number || seat.section}
              </Tag>
            )) || ['N/A'])}
        </div>
      ),
    },
    {
      title: 'Paid',
      key: 'paid',
      align: 'center',
      render: (_, record) => (
        <Tag
          color={record.paid ? 'green' : 'orange'}
          icon={record.paid ? <CheckCircleOutlined /> : null}
        >
          {record.paid ? 'Paid' : 'Unpaid'}
        </Tag>
      ),
    },
    {
      title: 'QR Verified',
      key: 'verified',
      align: 'center',
      render: (_, record) => (
        <Tag color={record.is_verified ? 'green' : 'default'}>
          {record.is_verified ? 'Verified' : 'Not Verified'}
        </Tag>
      ),
    },
    {
      title: 'Booked At',
      key: 'created_at',
      render: (_, record) => (
        <Text type="secondary">
          {new Date(record.created_at).toLocaleString('en-IN')}
        </Text>
      ),
    },
  ];

  if (loading && snapshots.length === 0) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div className="dashboard">
      <PageHeading name="Event Ticket Snapshots" icon={<CameraOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/event/shows')}
        style={{ marginBottom: 16 }}
      >
        Back to Show List
      </Button>

      {showInfo && (
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24}>
            <Card title="Event Summary" bordered={false}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Event">
                  <Title level={4} style={{ margin: 0 }}>{showInfo.eventHeading}</Title>
                </Descriptions.Item>
                <Descriptions.Item label="Venue">
                  <Text strong>{showInfo.venue}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Page">
                  Page {showInfo.currentPage} of {showInfo.totalPages} (Size: {showInfo.pageSize})
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  <Title level={3} style={{ margin: 0, color: '#1890ff' }}>{showInfo.count}</Title>
                </Descriptions.Item>
                <Descriptions.Item label="Tickets with QR">
                  <Tag color="green">{showInfo.qrCount}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Verified">
                  <Tag color="blue">{showInfo.verifiedCount || 0}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total Amount">
                  <Title level={4} style={{ margin: 0 }}>{showInfo.totalAmount}</Title>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      )}

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      }}>
        <Input
          placeholder="Search by Booking ID, Name, Email, Phone or Seat Number"
          prefix={<SearchOutlined />}
          style={{ width: '320px' }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination(prev => ({ ...prev, current: 1 }));
          }}
          allowClear
        />

        <Text type="secondary">
          Total: <strong>{pagination.total}</strong> ticket{pagination.total !== 1 ? 's' : ''}
        </Text>
      </div>

      {snapshots.length === 0 ? (
        <Empty description="No ticket snapshots for this event show time" />
      ) : (
        <>
          <Table
            dataSource={snapshots}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tickets`,
              onChange: handlePageChange,
              onShowSizeChange: (current, size) => handlePageChange(current, size),
            }}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No tickets found' }}
          />
        </>
      )}
    </div>
  );
};

export default EventTicketSnapshotsDetail;

